import { createClient, type SupabaseClient } from "@supabase/supabase-js";

/**
 * Supabase client (singleton).
 *
 * Uses the public **anon key** — RLS is the safety boundary, not key secrecy
 * (spec 05 §1, §6). Read endpoints are anon-accessible; mutations require an
 * authenticated session JWT.
 *
 * Requires VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY at build time.
 * Throws at import if missing so misconfiguration surfaces immediately.
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // Hard-fail in dev; the app cannot function without a backend.
  throw new Error(
    "Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. " +
      "Create apps/web/.env.local with both values (see Supabase project settings).",
  );
}

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Persist session in localStorage so returning users stay signed in.
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Passkeys (beta, opt-in) — spec 05 §1.
    flowType: "pkce",
  },
});

/** Optional: PMTiles basemap base URL on R2 (spec 06 §5). May be unset in dev. */
export const TILES_URL = import.meta.env.VITE_TILES_URL ?? "";
